import { useState } from 'react'
import { useTenantGroups } from '../../hooks/useTenantGroups'
import { useActionRunner } from '../../hooks/useActionRunner'
import { rebuildInventory } from '../../services/api/rebuildInventory'
import { ActionFeedback } from '../../components/ActionFeedback/ActionFeedback'
import { IspRebuildFields } from './IspRebuildFields'
import { EventRebuildFields } from './EventRebuildFields'
import { buildRebuildInventoryPayload } from './helpers/buildRebuildInventoryPayload'
import { deriveRebuildInventoryValidity } from './helpers/deriveRebuildInventoryValidity'
import type {
  RebuildInventoryFormState,
  RebuildMode,
} from './RebuildInventoryPage.types'
import '../../components/CreateEntityForm/CreateEntityForm.css'
import './RebuildInventoryPage.css'
import { sortSelectOptions } from '../../helpers/sortSelectOptions'

const modeOptions: { value: RebuildMode; label: string }[] = [
  { value: 'isp', label: 'By tenant group / inventory system type' },
  { value: 'event', label: 'By event' },
]

const initialState: RebuildInventoryFormState = {
  mode: '',
  tenantGroupCodes: [],
  inventorySystemType: '',
  sysEventIds: [],
  rebuildFilterPrices: false,
}

export function RebuildInventoryPage() {
  const { tenantGroups, isLoading: isLoadingGroups } = useTenantGroups()
  const { run, isSubmitting, feedback } = useActionRunner()
  const [state, setState] = useState<RebuildInventoryFormState>(initialState)

  const isValid = deriveRebuildInventoryValidity(state)
  const availableCodes = tenantGroups.map((group) => group.code)

  function update(patch: Partial<RebuildInventoryFormState>) {
    setState((prev) => ({ ...prev, ...patch }))
  }

  // Changing mode clears the mode-specific inputs so a switch never carries stale
  // values into the other mode's payload.
  function handleModeChange(mode: RebuildMode | '') {
    setState({ ...initialState, mode })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isValid || isSubmitting) {
      return
    }
    const body = buildRebuildInventoryPayload(state)
    await run((token) => rebuildInventory(token, body))
  }

  return (
    <div className="rebuild-inventory-page">
      <ActionFeedback feedback={feedback} />

      <div className="create-form-table mb-3">
        <div className="create-form-row--last">
          <div className="create-form-label">
            Rebuild By
            <span className="create-form-required" aria-hidden="true">
              *
            </span>
          </div>
          <div className="create-form-input-cell">
            <select
              className="form-select form-select-sm"
              aria-label="Rebuild mode"
              value={state.mode}
              onChange={(e) =>
                handleModeChange(e.target.value as RebuildMode | '')
              }
            >
              <option value="">Select…</option>
              {sortSelectOptions(modeOptions).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {state.mode !== '' && (
        <form onSubmit={handleSubmit}>
          {state.mode === 'isp' ? (
            <IspRebuildFields
              state={state}
              update={update}
              availableCodes={availableCodes}
              isLoadingGroups={isLoadingGroups}
            />
          ) : (
            <EventRebuildFields
              state={state}
              update={update}
              availableCodes={availableCodes}
              isLoadingGroups={isLoadingGroups}
            />
          )}

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
              )}
              Rebuild inventory
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
